/**
 * Survey Details Component
 * 
 * Displays detailed information about a specific survey and provides management options
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Share2,
  Play,
  Calendar,
  FileText,
  BarChart3,
  Copy,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  Clock,
  Edit3,
  Sparkles,
  Eye,
  Linkedin,
  Users,
  Settings,
  Image
} from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import { getSurveyById, updateSurveyState, surveyIdToShareId, getSurveyResponses, getSurveyMaxDiffSummary, getSurveyImageHotspotSummary, updateSurveyQuestions, updateSurveyWelcomeData, auditSurveyRedundancy, NEVER_SKIP_QUESTION_TYPES, validateSurvey, getLinkedInShareUrl, type SurveyBlockDefinition, type RedundancyCluster } from '../../utils/surveyApi';
import LinkedInVerificationsTable from './LinkedInVerificationsTable';
import { SURVEY_STATES } from '../../constants/surveyConstants';
import { UI_TEXT } from '../../config/ui-text';
import SurveyQuestionsPreview from './SurveyQuestionsPreview';
import SurveyValidationModal from './SurveyValidationModal';
import { useSurveyEditor, type EditorPending } from './useSurveyEditor';
import { SurveyValidationError, type ValidationReport } from '../../utils/surveyValidation';
import ImageHotspotHeatmap from '../../components/survey/ImageHotspotHeatmap';
import { formatDuration } from '../../lib/utils';

interface QualitySummary {
  total_responses: number;
  flagged_responses: number;
  flag_types: Record<string, number>;
  quality_score: number;
}

interface ResponseSummary {
  session_id: number;
  completed_at: string;
  responses_count: number;
  quality_summary?: QualitySummary;
  time_spent_seconds?: number | null;
}

interface Survey {
  id: number;
  name: string;
  title?: string;
  description?: string;
  instructions?: string;
  state: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  questions?: Array<{
    id: string;
    text: string;
    type: string;
    choices?: string[];
  }>;
  sessions_count?: number;
  responses_count?: number;
  max_responses?: number | null;
  min_linkedin_connections?: number | null;
  redirect_config?: {
    redirect_base_url: string;
    tracking_param: { [key: string]: string };
    completion_params: { [key: string]: string } | null;
    screen_out_params: { [key: string]: string } | null;
    quota_full_params: { [key: string]: string } | null;
  } | null;
  blocks?: { id: string; label: string; randomize_within: boolean }[] | null;
  ai_features?: {
    redundancy_skip_enabled?: boolean;
    redundancy_confidence_threshold?: number;
    // Per-survey toggle for rubric-driven adaptive follow-up probing. Default off.
    adaptive_probing?: boolean;
    // Experimental conversational / discussion-guide style.
    survey_style?: 'traditional' | 'conversational';
    discussion_guide?: {
      guide_markdown: string;
      mandatory_questions: { id: string; text: string }[];
      max_turns?: number;
    } | null;
  } | null;
}

const SurveyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [qualityData, setQualityData] = useState<ResponseSummary[]>([]);
  const [overallQualityStats, setOverallQualityStats] = useState<{
    total_sessions: number;
    total_responses: number;
    flagged_sessions: number;
    overall_quality_score: number;
    common_issues: Record<string, number>;
    average_time_spent_seconds: number | null;
  } | null>(null);
  const [averageTimeSpent, setAverageTimeSpent] = useState<number | null>(null);
  const [maxdiffData, setMaxdiffData] = useState<{
    survey_id: number;
    maxdiff_questions: Array<{
      question_id: string;
      question_text: string;
      total_sessions: number;
      total_sets: number;
      claims: Array<{
        claim: string;
        most_count: number;
        least_count: number;
        net_score: number;
        most_percentage: number;
        least_percentage: number;
        rank: number;
      }>;
    }>;
  } | null>(null);
  const [imageHotspotData, setImageHotspotData] = useState<{
    survey_id: number;
    image_hotspot_questions: Array<{
      question_id: string;
      question_text: string;
      image_url: string;
      bounding_boxes: Array<{
        id: string;
        label: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      ranking_count: number;
      total_sessions: number;
      total_clicks: number;
      aggregated_clicks: Array<{
        x: number;
        y: number;
        rank: number;
        product_id: string | null;
        product_label: string | null;
        timestamp_ms: number;
      }>;
      response_distribution: Array<{
        option: string;
        count: number;
        percentage: number;
        ranking_breakdown: Record<string, number>;
      }>;
    }>;
  } | null>(null);
  const [isEditingQuestions, setIsEditingQuestions] = useState(false);
  // Authoring-time redundancy audit (advisory)
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditClusters, setAuditClusters] = useState<RedundancyCluster[] | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [isSavingQuestions, setIsSavingQuestions] = useState(false);

  // LinkedIn verification state
  const [linkedInShareUrl, setLinkedInShareUrl] = useState<string>('');
  const [showLinkedInVerifications, setShowLinkedInVerifications] = useState(false);

  // Welcome screen editing state
  const [isEditingWelcome, setIsEditingWelcome] = useState(false);
  const [isSavingWelcome, setIsSavingWelcome] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedInstructions, setEditedInstructions] = useState('');

  useEffect(() => {
    loadSurvey();
  }, [id]);

  useEffect(() => {
    if (survey && survey.state === SURVEY_STATES.ACTIVE) {
      const shareId = surveyIdToShareId(survey.id);
      setShareUrl(`${window.location.origin}/surveys/${shareId}/take`);
      setLinkedInShareUrl(getLinkedInShareUrl(shareId));
    }
  }, [survey]);

  // Auto-run the redundancy audit once when a survey with questions loads, so the banner
  // shows on top in the survey view/preview without the author having to click.
  useEffect(() => {
    if (survey?.id && (survey.questions?.length || 0) >= 2 && auditClusters === null && !isAuditing && !auditError) {
      handleAuditRedundancy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey?.id]);

  const loadSurvey = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const result = await getSurveyById(parseInt(id));
      setSurvey(result.survey);

      // Populate the structural report so per-question badges + the activation gate reflect the
      // current state immediately (before the author makes any edit). Best-effort — a failed
      // dry-run must never block loading the survey; the PUT/activation paths re-validate anyway.
      const qs = result.survey.questions;
      if (Array.isArray(qs) && qs.length > 0) {
        try {
          const { report } = await validateSurvey(result.survey.id, qs, result.survey.blocks ?? undefined);
          validation.setReport(report);
        } catch { /* badges are a convenience */ }
      }

      // Load quality data if survey has responses
      if (result.survey.responses_count && result.survey.responses_count > 0) {
        await loadQualityData(parseInt(id));
      }
    } catch (error) {
      console.error('Failed to load survey:', error);
      setError(UI_TEXT.AI_SURVEY.DETAILS.ERRORS.FAILED_TO_LOAD);
    } finally {
      setIsLoading(false);
    }
  };

  const loadQualityData = async (surveyId: number) => {
    try {
      const responsesResult = await getSurveyResponses(surveyId);
      setQualityData(responsesResult.responses);

      // Calculate overall quality statistics
      let totalSessions = responsesResult.responses.length;
      let totalResponses = 0;
      let flaggedSessions = 0;
      let totalQualityScore = 0;
      let commonIssues: Record<string, number> = {};

      responsesResult.responses.forEach((session) => {
        if (session.quality_summary) {
          totalResponses += session.quality_summary.total_responses;
          if (session.quality_summary.flagged_responses > 0) {
            flaggedSessions++;
          }
          totalQualityScore += session.quality_summary.quality_score;

          // Aggregate flag types
          Object.entries(session.quality_summary.flag_types).forEach(([flag, count]) => {
            commonIssues[flag] = (commonIssues[flag] || 0) + count;
          });
        }
      });

      const overallQualityScore = totalSessions > 0 ? totalQualityScore / totalSessions : 100;

      setOverallQualityStats({
        total_sessions: totalSessions,
        total_responses: totalResponses,
        flagged_sessions: flaggedSessions,
        overall_quality_score: overallQualityScore,
        common_issues: commonIssues
      });

      // Calculate average time spent across all sessions
      let totalTimeSpent = 0;
      let sessionsWithTime = 0;
      responsesResult.responses.forEach((session) => {
        if (session.time_spent_seconds !== null && session.time_spent_seconds !== undefined) {
          totalTimeSpent += session.time_spent_seconds;
          sessionsWithTime++;
        }
      });
      const avgTime = sessionsWithTime > 0 ? totalTimeSpent / sessionsWithTime : null;
      setAverageTimeSpent(avgTime);

      // Load maxdiff data if available
      await loadMaxDiffData(surveyId);
      
      // Load image hotspot data if available
      await loadImageHotspotData(surveyId);
    } catch (error) {
      console.error('Failed to load quality data:', error);
      // Don't show error for quality data, it's supplementary
    }
  };

  const loadMaxDiffData = async (surveyId: number) => {
    try {
      const maxdiffResult = await getSurveyMaxDiffSummary(surveyId);
      // Only set data if there are maxdiff questions with results
      if (maxdiffResult.maxdiff_questions && maxdiffResult.maxdiff_questions.length > 0) {
        setMaxdiffData(maxdiffResult);
      }
    } catch (error) {
      console.error('Failed to load maxdiff data:', error);
      // Don't show error for maxdiff data, it's supplementary
    }
  };

  const loadImageHotspotData = async (surveyId: number) => {
    try {
      const hotspotResult = await getSurveyImageHotspotSummary(surveyId);
      // Only set data if there are image_hotspot questions with results
      if (hotspotResult.image_hotspot_questions && hotspotResult.image_hotspot_questions.length > 0) {
        setImageHotspotData(hotspotResult);
      }
    } catch (error) {
      console.error('Failed to load image hotspot data:', error);
      // Don't show error for image hotspot data, it's supplementary
    }
  };

  const handleStateChange = async (newState: string) => {
    if (!survey) return;

    try {
      setIsUpdating(true);
      setError(null);
      await updateSurveyState(survey.id, newState);
      setSurvey({ ...survey, state: newState });
    } catch (error) {
      // Activation gate: the backend refuses to go live while structural errors remain. Surface
      // the report so the offending questions get flagged inline, and tell the author to fix them.
      if (error instanceof SurveyValidationError) {
        validation.setReport(error.report);
        const n = error.report.errors?.length ?? 0;
        setError(`Fix ${n} structural issue${n === 1 ? '' : 's'} before activating this survey.`);
        return;
      }
      console.error('Failed to update survey state:', error);
      setError(UI_TEXT.AI_SURVEY.DETAILS.ERRORS.FAILED_TO_UPDATE);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleShare = () => {
    if (survey?.state !== SURVEY_STATES.ACTIVE) {
      setError(UI_TEXT.AI_SURVEY.DETAILS.ERRORS.MUST_BE_ACTIVE);
      return;
    }
    setShowShareModal(true);
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowShareModal(false);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleEditQuestionsClick = () => {
    if (!survey) return;

    setIsEditingQuestions(true);
  };

  // Keep the EARLIEST question in a cluster (by display order); later ELIGIBLE members are
  // the skippable duplicates. Never-skip types (matrices, rankings, scales, diaries…) are
  // never proposed for skipping — they appear advisory-only.
  const skipPlan = (cluster: RedundancyCluster, questions?: any[]): { primary: string; skipIds: string[] } => {
    const qs = questions || survey?.questions || [];
    const idxOf = (id: string) => qs.findIndex((q: any) => q.id === id);
    const typeOf = (id: string) => (qs.find((q: any) => q.id === id) as any)?.type;
    const ordered = [...cluster.question_ids].sort((a, b) => idxOf(a) - idxOf(b));
    const skipIds = ordered.slice(1).filter((id) => !NEVER_SKIP_QUESTION_TYPES.has(typeOf(id)));
    return { primary: ordered[0], skipIds };
  };

  // OPT-IN ONLY: running the audit writes NO skip flags. It only (a) refreshes the cluster
  // display, (b) lets the server persist info_targets, and (c) strips LEGACY auto-written
  // flags (anything without redundancy_optin — the old buggy flow wrote flags by default;
  // authors must re-opt-in deliberately per cluster).
  const handleAuditRedundancy = async () => {
    if (!survey?.questions) return;
    try {
      setIsAuditing(true);
      setAuditError(null);
      setAuditClusters(null);
      const result = await auditSurveyRedundancy(survey.id);
      // Server returns the questions WITH info_targets persisted — use as the base.
      let questions: any[] = (result.questions && result.questions.length
        ? result.questions : survey.questions) as any[];

      // Legacy cleanup: strip auto-written flags that were never explicitly accepted.
      let stripped = false;
      questions = questions.map((q: any) => {
        if ((q.redundancy_auto_skip || q.redundancy_covered_by) && q.redundancy_optin !== true) {
          stripped = true;
          const { redundancy_auto_skip, redundancy_covered_by, redundancy_cluster_id, ...rest } = q;
          return rest;
        }
        return q;
      });
      if (stripped) {
        await updateSurveyQuestions(survey.id, questions);
      }
      setSurvey({ ...survey, questions } as any);

      // A cluster is resolved when every skippable member is already decided
      // (kept, or explicitly opted into auto-skip).
      const decided = (id: string) => {
        const q: any = questions.find((x: any) => x.id === id);
        return q?.redundancy_keep === true || (q?.redundancy_auto_skip === true && q?.redundancy_optin === true);
      };
      const open = (result.clusters || []).filter((c) => {
        const { skipIds } = skipPlan(c, questions);
        return skipIds.length > 0 && !skipIds.every(decided);
      });
      setAuditClusters(open);
    } catch (e) {
      console.error('Redundancy audit failed:', e);
      setAuditError('Could not run the redundancy check. Please try again.');
    } finally {
      setIsAuditing(false);
    }
  };

  // EXPLICIT opt-in: author accepts auto-skipping this cluster's later duplicates. Even then,
  // the runtime only skips per-respondent after verifying their own answers cover the question.
  const handleAcceptCluster = async (cluster: RedundancyCluster) => {
    if (!survey?.questions) return;
    const { primary, skipIds } = skipPlan(cluster);
    if (!skipIds.length) return;
    const clusterId = [...cluster.question_ids].sort().join('|');
    const inCluster = new Set(cluster.question_ids);
    const toSkip = new Set(skipIds);
    const updated = (survey.questions as any[]).map((q: any) => {
      if (toSkip.has(q.id)) {
        return {
          ...q, redundancy_auto_skip: true, redundancy_covered_by: primary,
          redundancy_cluster_id: clusterId, redundancy_optin: true, redundancy_keep: false,
        };
      }
      if (inCluster.has(q.id)) {
        // primary (and never-skip members): carry the cluster id so randomized order still
        // links candidates to their answered siblings.
        return { ...q, redundancy_cluster_id: clusterId };
      }
      return q;
    });
    try {
      await updateSurveyQuestions(survey.id, updated);
      setSurvey({ ...survey, questions: updated } as any);
      setAuditClusters(prev => (prev || []).filter(c => c !== cluster));
    } catch (e) {
      console.error('Failed to save auto-skip choice:', e);
      setAuditError('Could not save your choice. Please try again.');
    }
  };

  // "Dismiss" → ask everything in this cluster; resolves it so it stops nagging.
  const handleIgnoreCluster = async (cluster: RedundancyCluster) => {
    if (!survey?.questions) return;
    const ids = new Set(skipPlan(cluster).skipIds);
    const updated = (survey.questions as any[]).map((q: any) =>
      ids.has(q.id)
        ? { ...q, redundancy_keep: true, redundancy_auto_skip: false, redundancy_optin: false }
        : q);
    try {
      await updateSurveyQuestions(survey.id, updated);
      setSurvey({ ...survey, questions: updated } as any);
      setAuditClusters(prev => (prev || []).filter(c => c !== cluster));
    } catch (e) {
      console.error('Failed to save dismiss decision:', e);
      setAuditError('Could not save your choice. Please try again.');
    }
  };

  // Save-always editor: edits persist immediately; the returned report drives per-question badges
  // and gates ACTIVATION (not the save). Only a destructive live-response edit opens the modal.
  const validation = useSurveyEditor<EditorPending, { survey: Survey; report?: ValidationReport | null }>({
    validate: (questions, blocks, opts) =>
      validateSurvey(survey!.id, questions as unknown[], blocks ?? undefined, opts),
    save: (pending, ack) => {
      // One "Adaptive features" switch drives all three adaptive flags; the legacy redundancy-skip
      // system is disabled (it's no longer surfaced in the editor).
      const adaptive = pending.adaptiveProbing as boolean;
      return updateSurveyQuestions(
        survey!.id, pending.questions, pending.redirectConfig, pending.maxResponses as number | null,
        pending.blocks as SurveyBlockDefinition[] | null,
        pending.minLinkedInConnections as number | null, false,
        undefined, undefined, undefined, undefined, ack, adaptive, adaptive, adaptive
      );
    },
    applyResult: (result) => setSurvey(result.survey),
    extractReport: (result) => result.report ?? null,
    onError: (msg) => setError(msg),
  });

  const handleQuestionsUpdated = async (
    updatedQuestions: any[],
    redirectConfig?: any,
    maxResponses?: number | null,
    blocks?: SurveyBlockDefinition[] | null,
    enableAiRedundancySkip?: boolean,
    adaptiveProbing?: boolean,
    minLinkedInConnections?: number | null
  ) => {
    if (!survey) return;
    setIsSavingQuestions(true);
    setError(null);
    await validation.saveQuestions({ questions: updatedQuestions, redirectConfig, maxResponses, blocks, enableAiRedundancySkip, adaptiveProbing, minLinkedInConnections });
    setIsSavingQuestions(false);
  };

  const handleEditWelcomeClick = () => {
    if (!survey) return;

    setEditedTitle(survey.title || '');
    setEditedDescription(survey.description || '');
    setEditedInstructions(survey.instructions || '');
    setIsEditingWelcome(true);
  };

  const handleSaveWelcome = async () => {
    if (!survey) return;

    try {
      setIsSavingWelcome(true);
      setError(null);

      const result = await updateSurveyWelcomeData(survey.id, {
        title: editedTitle,
        description: editedDescription,
        instructions: editedInstructions,
      });

      setSurvey(result.survey);
      setIsEditingWelcome(false);
    } catch (error) {
      console.error('Failed to save welcome data:', error);
      setError('Failed to save welcome screen data. Please try again.');
    } finally {
      setIsSavingWelcome(false);
    }
  };

  const handleCancelWelcomeEdit = () => {
    setIsEditingWelcome(false);
    setEditedTitle('');
    setEditedDescription('');
    setEditedInstructions('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case SURVEY_STATES.DRAFT:
        return 'bg-gray-100 text-gray-800';
      case SURVEY_STATES.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SURVEY_STATES.PAUSED:
        return 'bg-yellow-100 text-yellow-800';
      case SURVEY_STATES.COMPLETED:
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  const getQualityScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getQualityLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  const getFlagLabel = (flag: string) => {
    switch (flag) {
      case 'rapid_completion':
        return UI_TEXT.QUALITY_FLAGS?.RAPID_COMPLETION || 'Rapid Completion';
      case 'very_short_response':
        return UI_TEXT.QUALITY_FLAGS?.VERY_SHORT_RESPONSE || 'Very Short Response';
      default:
        return flag.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const renderMaxDiffSummary = () => {
    if (!maxdiffData || !maxdiffData.maxdiff_questions || maxdiffData.maxdiff_questions.length === 0) {
      return null;
    }

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            MaxDiff Results Summary
          </h2>
        </div>

        {maxdiffData.maxdiff_questions.map((question, questionIndex) => {
          const totalClaims = question.claims.length;

          const getClaimRowColor = (rank: number): string => {
            if (rank <= 3) return 'bg-green-50';
            if (rank > totalClaims - 3) return 'bg-red-50';
            return 'bg-white';
          };

          return (
            <div key={question.question_id} className={questionIndex > 0 ? 'mt-8' : ''}>
              <div className="mb-4">
                <h3 className="text-base font-medium text-gray-900 mb-2">{question.question_text}</h3>
                <p className="text-sm text-gray-600">
                  Aggregated rankings based on {question.total_sessions} session{question.total_sessions !== 1 ? 's' : ''} with {question.total_sets} set{question.total_sets !== 1 ? 's' : ''} completed. Net Score = (Most Selected) - (Least Selected)
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Claim
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 mr-1 text-green-600" />
                          Most Selected
                        </div>
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-center">
                          <TrendingDown className="w-4 h-4 mr-1 text-red-600" />
                          Least Selected
                        </div>
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Net Score
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {question.claims.map((claim, index) => (
                      <tr key={index} className={`${getClaimRowColor(claim.rank)} hover:opacity-75 transition-opacity`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className={`text-sm font-semibold ${
                              claim.rank <= 3 ? 'text-green-700' :
                              claim.rank > totalClaims - 3 ? 'text-red-700' :
                              'text-gray-700'
                            }`}>
                              #{claim.rank}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-md">{claim.claim}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-medium text-gray-900">{claim.most_count}</div>
                          <div className="text-xs text-gray-500">({claim.most_percentage.toFixed(1)}%)</div>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-green-600 h-1.5 rounded-full"
                              style={{ width: `${Math.min(claim.most_percentage, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-medium text-gray-900">{claim.least_count}</div>
                          <div className="text-xs text-gray-500">({claim.least_percentage.toFixed(1)}%)</div>
                          <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-red-600 h-1.5 rounded-full"
                              style={{ width: `${Math.min(claim.least_percentage, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                            claim.net_score > 0 ? 'bg-green-100 text-green-800' :
                            claim.net_score < 0 ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {claim.net_score > 0 ? '+' : ''}{claim.net_score}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderImageHotspotSummary = () => {
    if (!imageHotspotData || !imageHotspotData.image_hotspot_questions || imageHotspotData.image_hotspot_questions.length === 0) {
      return null;
    }

    const API_URL = import.meta.env.VITE_API_URL || '';

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            Image Hotspot Results Summary
          </h2>
        </div>

        {imageHotspotData.image_hotspot_questions.map((question, questionIndex) => (
          <div key={question.question_id} className={questionIndex > 0 ? 'mt-8 pt-8 border-t border-gray-200' : ''}>
            <div className="mb-4">
              <h3 className="text-base font-medium text-gray-900 mb-2">{question.question_text}</h3>
              <p className="text-sm text-gray-600">
                Aggregated click data from {question.total_sessions} session{question.total_sessions !== 1 ? 's' : ''} with {question.total_clicks} total clicks
              </p>
            </div>

            {/* Image Hotspot Heatmap */}
            {question.image_url && (
              <ImageHotspotHeatmap
                imageUrl={question.image_url.startsWith('http') 
                  ? question.image_url 
                  : `${API_URL}${question.image_url}`}
                clicks={question.aggregated_clicks}
                boundingBoxes={question.bounding_boxes}
                totalRespondents={question.total_sessions}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{UI_TEXT.AI_SURVEY.DETAILS.LOADING.LOADING}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center py-8">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {UI_TEXT.AI_SURVEY.DETAILS.TITLES.SURVEY_NOT_FOUND}
              </h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={() => navigate('/surveys')}
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.BACK_TO_SURVEYS}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/surveys')}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.BACK_TO_SURVEYS}
          </Button>
          
          <div className="flex items-center space-x-3">
            {survey.state === SURVEY_STATES.DRAFT && (
              <Button
                variant="primary"
                onClick={() => handleStateChange(SURVEY_STATES.ACTIVE)}
                disabled={isUpdating || validation.errorCount > 0}
                icon={<Play className="w-4 h-4" />}
                title={validation.errorCount > 0
                  ? `Fix ${validation.errorCount} structural issue${validation.errorCount === 1 ? '' : 's'} to activate`
                  : undefined}
              >
                {validation.errorCount > 0
                  ? `Fix ${validation.errorCount} issue${validation.errorCount === 1 ? '' : 's'} to activate`
                  : UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.PUBLISH}
              </Button>
            )}

            {survey.state === SURVEY_STATES.ACTIVE && (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/surveys/${survey.id}/responses`)}
                  icon={<BarChart3 className="w-4 h-4" />}
                >
                  {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.VIEW_RESPONSES}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/surveys/${survey.id}/analysis`)}
                  icon={<Sparkles className="w-4 h-4" />}
                >
                  AI Analysis
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/surveys/${survey.id}/chat`)}
                  icon={<MessageSquare className="w-4 h-4" />}
                >
                  Chat with Survey
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/surveys/${survey.id}/images`)}
                  icon={<Image className="w-4 h-4" />}
                >
                  View Images
                </Button>
                <Button
                  variant="primary"
                  onClick={handleShare}
                  icon={<Share2 className="w-4 h-4" />}
                >
                  {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.SHARE}
                </Button>
              </>
            )}

          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Survey Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <FileText className="w-6 h-6 text-blue-500 mr-2" />
                    <h1 className="text-2xl font-bold text-gray-900">
                      {survey.name}
                    </h1>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStateColor(survey.state)}`}>
                      {survey.state}
                    </span>
                    {survey.state === SURVEY_STATES.ACTIVE && (
                      <a
                        href={`/surveys/${surveyIdToShareId(survey.id)}/take`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.VIEW_LIVE}
                      </a>
                    )}
                    <a
                      href={`/surveys/${surveyIdToShareId(survey.id)}/take?preview=true`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview Survey
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">{UI_TEXT.AI_SURVEY.DETAILS.LABELS.CREATED}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(survey.created_at)}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">{UI_TEXT.AI_SURVEY.DETAILS.LABELS.QUESTIONS}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {survey.questions?.length || 0}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500 mb-1">{UI_TEXT.AI_SURVEY.DETAILS.LABELS.RESPONSES}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {survey.responses_count || 0}
                  </p>
                </div>
              </div>

            </div>

            {/* Response Quality Statistics */}
            {overallQualityStats && overallQualityStats.total_sessions > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Response Quality Overview
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className={`w-5 h-5 mx-auto mb-1 ${overallQualityStats.overall_quality_score >= 80 ? 'text-green-500' : overallQualityStats.overall_quality_score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {overallQualityStats.overall_quality_score >= 80 ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-1">Quality Score</p>
                    <p className={`text-sm font-medium ${getQualityScoreColor(overallQualityStats.overall_quality_score)}`}>
                      {Math.round(overallQualityStats.overall_quality_score)}% - {getQualityLabel(overallQualityStats.overall_quality_score)}
                    </p>
                  </div>

                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 mb-1">Flagged Sessions</p>
                    <p className="text-sm font-medium text-gray-900">
                      {overallQualityStats.flagged_sessions}
                      <span className="text-xs text-gray-500 ml-1">
                        ({overallQualityStats.total_sessions > 0 ? Math.round((overallQualityStats.flagged_sessions / overallQualityStats.total_sessions) * 100) : 0}%)
                      </span>
                    </p>
                  </div>

                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 mb-1">Average Time</p>
                    <p className="text-sm font-medium text-gray-900">
                      {averageTimeSpent !== null ? formatDuration(averageTimeSpent) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* MaxDiff Results Summary */}
            {renderMaxDiffSummary()}

            {/* Image Hotspot Results Summary */}
            {renderImageHotspotSummary()}

            {/* Welcome Screen Configuration */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Welcome Screen
                </h2>
                {!isEditingWelcome && (
                  <Button
                    variant="outline"
                    onClick={handleEditWelcomeClick}
                    icon={<Edit3 className="w-4 h-4" />}
                  >
                    Edit
                  </Button>
                )}
              </div>

              {isEditingWelcome ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      placeholder="Enter survey title..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The main title shown on the welcome screen
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder="Enter survey description..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A brief description of the survey purpose
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instructions
                    </label>
                    <textarea
                      value={editedInstructions}
                      onChange={(e) => setEditedInstructions(e.target.value)}
                      placeholder="Enter survey instructions..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Instructions for completing the survey
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={handleCancelWelcomeEdit}
                      disabled={isSavingWelcome}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSaveWelcome}
                      disabled={isSavingWelcome}
                    >
                      {isSavingWelcome ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Title</p>
                    <p className="text-sm text-gray-900">
                      {survey.title || <span className="text-gray-400 italic">Not set</span>}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {survey.description || <span className="text-gray-400 italic">Not set</span>}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">Instructions</p>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {survey.instructions || <span className="text-gray-400 italic">Not set</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* LinkedIn Verification Settings */}
            {survey.min_linkedin_connections && survey.min_linkedin_connections > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Linkedin className="w-5 h-5 mr-2 text-blue-600" />
                    LinkedIn Verification
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLinkedInVerifications(!showLinkedInVerifications)}
                    icon={showLinkedInVerifications ? <Eye className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                  >
                    {showLinkedInVerifications ? 'Hide' : 'View'} Verifications
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">
                        {survey.min_linkedin_connections.toLocaleString()}+ connections required
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-blue-800 mt-2">
                    Respondents using the LinkedIn link must have at least {survey.min_linkedin_connections.toLocaleString()} LinkedIn connections to access this survey.
                  </p>
                </div>

                {showLinkedInVerifications && (
                  <LinkedInVerificationsTable surveyId={survey.id} />
                )}
              </div>
            )}

            {/* Questions Preview — also rendered for conversational surveys (which may have no
                fixed questions) so the discussion guide can be edited. */}
            {(survey.questions && survey.questions.length > 0) || survey.ai_features?.survey_style === 'conversational' ? (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    {survey.ai_features?.survey_style === 'conversational' ? 'Discussion Guide' : 'Survey'}
                  </h2>
                  {!isEditingQuestions && (
                    <div className="flex items-center gap-2">
                      {/* Redundancy audit applies to fixed questions only. */}
                      {survey.ai_features?.survey_style !== 'conversational' && (
                        <Button
                          variant="outline"
                          onClick={handleAuditRedundancy}
                          disabled={isAuditing}
                          icon={<Sparkles className="w-4 h-4" />}
                        >
                          {isAuditing ? 'Checking…' : 'Check for redundant questions'}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={handleEditQuestionsClick}
                        icon={<Edit3 className="w-4 h-4" />}
                      >
                        Edit
                      </Button>
                    </div>
                  )}
                </div>

                {/* Redundancy audit results (advisory) */}
                {auditError && (
                  <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    {auditError}
                  </div>
                )}
                {auditClusters !== null && !auditError && (
                  <div className="mb-4">
                    {auditClusters.length === 0 ? (
                      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> No redundant questions detected.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                          <Sparkles className="w-4 h-4" />
                          {auditClusters.length} group{auditClusters.length === 1 ? '' : 's'} of overlapping questions
                        </div>
                        <div className="text-xs text-amber-700 mb-3">
                          Advisory only — <strong>nothing is skipped unless you accept it</strong>. If you do
                          nothing, every question is asked. Even after you accept, a duplicate is only skipped
                          for respondents whose own earlier answers verifiably cover it.
                        </div>
                        <div className="space-y-3">
                          {auditClusters.map((c, i) => {
                            const { primary, skipIds } = skipPlan(c);
                            return (
                              <div key={i} className="bg-white border border-amber-100 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    c.severity === 'duplicate'
                                      ? 'bg-red-100 text-red-700'
                                      : c.severity === 'strong'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-gray-100 text-gray-700'
                                  }`}>{c.severity}</span>
                                  <span className="text-sm font-medium text-gray-900">{c.theme}</span>
                                </div>
                                <div className="text-xs text-gray-600 mb-1">
                                  Questions: <span className="font-medium">{c.question_ids.join(', ')}</span>
                                  {skipIds.length > 0 && (
                                    <> — if accepted, <span className="font-medium">{skipIds.join(', ')}</span> may
                                    be skipped when covered (keeping <span className="font-medium">{primary}</span>)</>
                                  )}
                                </div>
                                <div className="text-sm text-gray-700 mb-2">{c.suggestion}</div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Button variant="outline" size="sm"
                                    onClick={() => { handleEditQuestionsClick(); }}
                                    icon={<Edit3 className="w-3.5 h-3.5" />}>
                                    Edit to consolidate
                                  </Button>
                                  {skipIds.length > 0 && c.severity !== 'mild' && (
                                    <Button variant="outline" size="sm" onClick={() => handleAcceptCluster(c)}
                                      icon={<Sparkles className="w-3.5 h-3.5" />}>
                                      Auto-skip duplicates
                                    </Button>
                                  )}
                                  <Button variant="ghost" size="sm" onClick={() => handleIgnoreCluster(c)}>
                                    Dismiss (ask all)
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-xs text-gray-500 mt-3">
                          Nothing is deleted or changed automatically — your questions stay as written until you act.
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <SurveyQuestionsPreview
                  parsedQuestions={survey.questions || []}
                  surveyName={survey.name}
                  focusQuestionId={validation.focusQuestionId}
                  onFocusQuestionHandled={validation.clearFocusQuestion}
                  issuesByQuestion={validation.issuesByQuestion}
                  onQuestionsUpdated={handleQuestionsUpdated}
                  onCreateSurvey={async () => {}}
                  onGoBack={() => setIsEditingQuestions(false)}
                  isLoading={isSavingQuestions}
                  readOnly={!isEditingQuestions}
                  hideActionButtons={true}
                  initialMaxResponses={survey.max_responses}
                  initialAdaptiveProbing={survey.ai_features?.adaptive_probing ?? false}
                  initialSurveyStyle={survey.ai_features?.survey_style ?? 'traditional'}
                  initialDiscussionGuide={survey.ai_features?.discussion_guide ?? null}
                  initialMinLinkedInConnections={survey.min_linkedin_connections}
                  initialRedirectConfig={survey.redirect_config}
                  initialBlocks={survey.blocks ?? []}
                  surveyId={survey.id}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">{UI_TEXT.AI_SURVEY.DETAILS.DESCRIPTIONS.NO_QUESTIONS}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {UI_TEXT.AI_SURVEY.DETAILS.TITLES.SHARE_SURVEY}
              </h3>

              <p className="text-gray-600 mb-4">
                {UI_TEXT.AI_SURVEY.DETAILS.DESCRIPTIONS.SHARE_HELP}
              </p>

              {/* Regular Share Link */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regular Link
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
                    icon={<ExternalLink className="w-4 h-4" />}
                  >
                    Open
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={copyShareUrl}
                    icon={<Copy className="w-4 h-4" />}
                  >
                    {UI_TEXT.AI_SURVEY.DETAILS.BUTTONS.COPY}
                  </Button>
                </div>
              </div>

              {/* LinkedIn Share Link (only show if LinkedIn verification is enabled) */}
              {survey?.min_linkedin_connections && survey.min_linkedin_connections > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-600" />
                    LinkedIn Verified Link
                    <span className="text-xs text-gray-500 font-normal">
                      (Requires {survey.min_linkedin_connections}+ connections)
                    </span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={linkedInShareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border border-blue-200 rounded-md bg-blue-50 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(linkedInShareUrl, '_blank', 'noopener,noreferrer')}
                      icon={<ExternalLink className="w-4 h-4" />}
                    >
                      Open
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={async () => {
                        await navigator.clipboard.writeText(linkedInShareUrl);
                      }}
                      icon={<Copy className="w-4 h-4" />}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Users clicking this link must verify their LinkedIn profile before accessing the survey.
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowShareModal(false)}
                >
                  {UI_TEXT.BUTTONS.CLOSE}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {validation.confirmReport && (
        <SurveyValidationModal
          report={validation.confirmReport}
          canOverride={true}
          busy={validation.busy}
          onFixAll={validation.fixAllAndConfirm}
          onAutoFix={validation.autoFix}
          onGoToQuestion={validation.goToQuestion}
          onSaveAnyway={validation.confirmSave}
          onCancel={validation.cancelConfirm}
        />
      )}
    </div>
    </Layout>
  );
};

export default SurveyDetails;